import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { CanChangeSingleInvoiceBudgetPeriod } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import useConvertAllocationApi from 'src/services/useConvertAllocationApi';
import useInvoiceMultiPeriodAllocationUpdateApi from 'src/services/useInvoiceMultiPeriodAllocationUpdateApi';
import { hasPermission } from 'src/utils/JWT';
import { useContextSelector } from 'use-context-selector';
import { AssignMultipleButtonPermissions, AssignSingleButtonPermissions } from '.';
import { InvoiceContext } from '../invoice_details/InvoiceDetailsContext';
import InvoiceBudgetPeriodInfo from './InvoiceBudgetPeriodInfo';
import InvoiceBudgetPeriodNavBar from './InvoiceBudgetPeriodNavBar';

const ViewInvoiceBudgetPeriodActions = () => {
  const budgetPeriodsHasErrors = useContextSelector(
    InvoiceContext,
    (value) => value.budgetPeriodsHasErrors
  );
  const saveErrorList = useContextSelector(InvoiceContext, (value) => value.saveErrorList);
  const errorMessageList = useContextSelector(InvoiceContext, (value) => value.errorMessageList);
  const invoiceBudgetPeriods = useContextSelector(
    InvoiceContext,
    (value) => value.invoiceBudgetPeriods
  );

  const setDistributionType = useContextSelector(
    InvoiceContext,
    (value) => value.setDistributionType
  );
  const closeMpa = useContextSelector(InvoiceContext, (value) => value.closeMpa);
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const invoiceId = useContextSelector(InvoiceContext, (value) => value.invoiceId);
  const getInvoice = useContextSelector(InvoiceContext, (value) => value.getInvoice);
  const invoiceGlCodes = useContextSelector(InvoiceContext, (value) => value.invoiceGlCodes);
  const setInvoiceGlCodes = useContextSelector(InvoiceContext, (value) => value.setInvoiceGlCodes);
  const mpaPermissionNumber = useContextSelector(
    InvoiceContext,
    (value) => value.mpaPermissionNumber
  );
  const calculateGLDistributionData = useContextSelector(
    InvoiceContext,
    (value) => value.calculateGLDistributionData
  );
  const getInvoiceGlBudgetSummary = useContextSelector(
    InvoiceContext,
    (value) => value.getInvoiceGlBudgetSummary
  );
  const isInvoiceBudgetPeriodDirty = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceBudgetPeriodDirty
  );

  const saveInvoice = useContextSelector(InvoiceContext, (value) => value.saveInvoice);
  const singleGlPeriodStartDate = useContextSelector(
    InvoiceContext,
    (value) => value.singleGlPeriodStartDate
  );

  const { updateMultiPeriodAllocation: invoiceUpdateMultiPeriodAllocation, isMultiPeriodLoading } =
    useInvoiceMultiPeriodAllocationUpdateApi(Number(invoiceId));
  const handleCancelSidePanel = useContextSelector(
    InvoiceContext,
    (value) => value.handleCancelSidePanel
  );
  const resetMPA = useCallback(async () => {
    closeMpa();

    setDistributionType('Percentage');
    toast.success('Accounting Period(s) saved successfully!');
    await getInvoice();
    await getInvoiceGlBudgetSummary();
  }, [closeMpa, getInvoice, getInvoiceGlBudgetSummary, setDistributionType]);

  const isSinglePeriodAssign = hasPermission(AssignSingleButtonPermissions, mpaPermissionNumber);
  const isMultiplePeriodAssign = hasPermission(
    AssignMultipleButtonPermissions,
    mpaPermissionNumber
  );

  const { convertAllocation, isLoading: isConvertLoading } = useConvertAllocationApi(
    Number(invoiceId),
    !isSinglePeriodAssign
  );

  const isEditable = usePermission({ permissions: CanChangeSingleInvoiceBudgetPeriod });

  const handleSave = useCallback(async () => {
    if (isSinglePeriodAssign || isMultiplePeriodAssign) {
      if (isEditable && invoiceBudgetPeriods.length === 1) {
        await saveInvoice(singleGlPeriodStartDate);
      } else {
        await convertAllocation();
      }
      resetMPA();
    } else {
      const GlDistributionData = calculateGLDistributionData(invoiceGlCodes);
      setInvoiceGlCodes(GlDistributionData);

      invoiceUpdateMultiPeriodAllocation(
        invoiceBudgetPeriods.map((item) => ({
          amount: item.amount,
          glPeriodId: item.glPeriodId,
          isClosed: item.isClosed as number,
          percentage: item.percentageAmount,
        }))
      ).then(async (res) => {
        if (res.success) {
          resetMPA();
        }
      });
    }
  }, [
    calculateGLDistributionData,
    convertAllocation,
    invoiceBudgetPeriods,
    invoiceGlCodes,
    invoiceUpdateMultiPeriodAllocation,
    isEditable,
    isMultiplePeriodAssign,
    isSinglePeriodAssign,
    resetMPA,
    saveInvoice,
    setInvoiceGlCodes,
    singleGlPeriodStartDate,
  ]);

  const { errorList, isSaveDisabled, showErrorPopUp } = useMemo(() => {
    const errorList = [...saveErrorList, ...errorMessageList];
    const isSaveDisabled =
      budgetPeriodsHasErrors ||
      !isInvoiceBudgetPeriodDirty ||
      invoiceBudgetPeriods.some((v) => !v.amount || !v.glPeriodId);
    const showErrorPopUp = budgetPeriodsHasErrors;

    return { errorList, isSaveDisabled, showErrorPopUp };
  }, [
    saveErrorList,
    errorMessageList,
    budgetPeriodsHasErrors,
    isInvoiceBudgetPeriodDirty,
    invoiceBudgetPeriods,
  ]);

  return (
    <header>
      <InvoiceBudgetPeriodNavBar
        onCancel={handleCancelSidePanel}
        onSave={handleSave}
        isSaveDisabled={isSaveDisabled}
        errorList={errorList}
        showErrorPopUp={showErrorPopUp}
        isSaveLoading={isMultiPeriodLoading || isConvertLoading}
      />
      <InvoiceBudgetPeriodInfo
        detailsInfo={invoice}
        isGrandTotalNotMatch={errorMessageList.length > 0}
      />
    </header>
  );
};

export default ViewInvoiceBudgetPeriodActions;
